/**
 * Tag Editor Change Service
 * Handles change emission with proper logic (silent vs normal, debouncing)
 */

import { Injectable } from '@angular/core'
import { COMPONENT_STRINGS, HTML_ATTRIBUTES } from '../tag-editor.constants'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { InsertedTagCustom } from '../tag-editor.model'

/**
 * Options for change emission
 */
export interface ChangeOptions {
  readonly silent?: boolean
  readonly updateStore?: boolean
  readonly updateForm?: boolean
  readonly emitEvents?: boolean
}

/**
 * Callbacks for change emission
 */
export interface ChangeCallbacks {
  readonly onValueChange: (value: string) => void
  readonly onTouched: () => void
  readonly onValueChangeEvent: (value: string) => void
  readonly onTagDataChangeEvent: (content: string, tags: ReadonlyArray<InsertedTagCustom>) => void
  readonly onStoreUpdate?: (hasTags: boolean) => void
}

@Injectable({ providedIn: 'root' })
export class TagEditorChangeService {
  constructor(
    private readonly stateService: TagEditorStateService,
    private readonly debugLoggingService: DebugLoggingService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  /**
   * Silently updates internal value without triggering change detection
   */
  public emitChangeSilent(content: string, callbacks: ChangeCallbacks): void {
    callbacks.onValueChange(content)
    this.stateService.setHtmlContent(content)
    this.debugLog(' Silently updated value (no change detection):', {
      contentLength: content.length,
      hasTagAttributes: content.includes(HTML_ATTRIBUTES.DATA_TAG_ID),
    })
  }

  /**
   * Emits changes to Angular forms and parent components
   */
  public emitChange(content: string, callbacks: ChangeCallbacks, options: ChangeOptions = {}): void {
    if (this.stateService.getEditingTag()) {
      this.debugLog(' ERROR: emitChange called during inline editing - using silent emit instead')
      this.emitChangeSilent(content, callbacks)
      return
    }

    const { silent = false, updateForm = true, emitEvents = true, updateStore = true } = options

    if (silent) {
      this.emitChangeSilent(content, callbacks)
      return
    }

    callbacks.onValueChange(content)
    this.stateService.setHtmlContent(content)

    if (updateForm) {
      callbacks.onTouched()
    }

    if (emitEvents) {
      callbacks.onValueChangeEvent(content)
    }

    if (updateStore && callbacks.onStoreUpdate) {
      // Store update will be handled by component (needs tags)
    }

    this.debugLog(' Emitted change:', {
      contentLength: content.length,
      updateForm,
      emitEvents,
      updateStore,
    })
  }

  /**
   * Checks if change should be emitted
   */
  public shouldEmit(editingTag: unknown): boolean {
    return !editingTag
  }
}
